import { create } from "zustand";
import { buildApiUrl } from "@/lib/api";

interface RoleSummary {
	id?: number;
	name?: string;
}

interface EntitySummary {
	id?: number;
	name?: string;
}

export interface SessionUser {
	id: number;
	email: string;
	firstName?: string;
	lastName?: string;
	role?: RoleSummary;
	entity?: EntitySummary;
}

export type MfaChallenge = {
	mfaRequired: true;
	challengeId: string;
	expiresAt: string;
};

type SessionStatus = "checking" | "authenticated" | "unauthenticated";

interface SessionState {
	challenge: MfaChallenge | null;
	verifyMfa: (
		code: string,
		recovery: boolean,
		language: string,
	) => Promise<void>;
	cancelMfa: () => void;
	status: SessionStatus;
	user: SessionUser | null;
	message?: string;
	initialize: () => Promise<void>;
	login: (
		payload: { email: string; password: string },
		language?: string,
	) => Promise<boolean>;
	logout: () => Promise<void>;
}

async function parseResponse<T>(response: Response): Promise<T> {
	const payload = await response.json();
	if (response.ok) {
		return payload as T;
	}
	const message =
		typeof payload?.message === "string"
			? payload.message
			: "Unexpected error occurred.";
	throw new Error(message);
}

const STORAGE_KEY = "audit-log-is-authenticated";

const storage = (() => {
	if (typeof window === "undefined") return undefined;
	try {
		return window.localStorage;
	} catch {
		return undefined;
	}
})();

const setAuthFlag = (value: boolean) => {
	if (!storage) return;
	try {
		storage.setItem(STORAGE_KEY, value ? "true" : "false");
	} catch {
		// ignore storage write errors
	}
};

const getAuthFlag = (): boolean => {
	if (!storage) return false;
	try {
		return storage.getItem(STORAGE_KEY) === "true";
	} catch {
		return false;
	}
};

const clearAuthFlag = () => setAuthFlag(false);

export const useSessionStore = create<SessionState>((set, get) => ({
	challenge: null,
	cancelMfa: () => {
		clearAuthFlag();
		set({ challenge: null, status: "unauthenticated", user: null });
	},
	verifyMfa: async (code, recovery, language) => {
		const challenge = get().challenge;
		if (!challenge) throw new Error("No pending login");
		const response = await fetch(buildApiUrl("/api/auth/mfa/verify"), {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				"X-MFA-Request": "1",
				"Accept-Language": language,
			},
			body: JSON.stringify({
				challengeId: challenge.challengeId,
				code,
				recovery,
				audience: "audit",
			}),
		});
		const payload = await parseResponse<{ data?: SessionUser }>(response);
		if (!payload.data?.id) throw new Error("Missing session");
		setAuthFlag(true);
		set({
			status: "authenticated",
			user: payload.data,
			challenge: null,
			message: undefined,
		});
	},
	status: getAuthFlag() ? "checking" : "unauthenticated",
	user: null,
	message: undefined,
	initialize: async () => {
		if (!getAuthFlag()) {
			set({
				status: "unauthenticated",
				user: null,
				message: undefined,
			});
			return;
		}

		set((state) => ({
			...state,
			status: "checking",
		}));

		try {
			const response = await fetch(buildApiUrl("/api/session/me"), {
				credentials: "include",
			});

			if (response.status === 401 || response.status === 403) {
				clearAuthFlag();
				set({
					status: "unauthenticated",
					user: null,
					message: undefined,
				});
				return;
			}

			const payload = await parseResponse<{
				data?: SessionUser;
			}>(response);

			if (!payload.data) {
				clearAuthFlag();
				set({
					status: "unauthenticated",
					user: null,
					message: undefined,
				});
				return;
			}

			set({
				status: "authenticated",
				user: payload.data,
				message: undefined,
			});
		} catch (error) {
			clearAuthFlag();
			set({
				status: "unauthenticated",
				user: null,
				message:
					error instanceof Error
						? error.message
						: "Unable to verify current session.",
			});
		}
	},
	login: async (credentials, language = "en") => {
		set({ message: undefined });
		const response = await fetch(buildApiUrl("/api/auth/audit/login"), {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				"Accept-Language": language,
			},
			body: JSON.stringify(credentials),
		});

		const payload = await parseResponse<{ data?: SessionUser | MfaChallenge }>(
			response,
		);
		if (payload.data && "mfaRequired" in payload.data) {
			clearAuthFlag();
			set({ status: "unauthenticated", user: null, challenge: payload.data });
			return false;
		}

		if (!payload.data) {
			throw new Error("Login failed. No user information returned.");
		}

		setAuthFlag(true);

		set({
			status: "authenticated",
			user: payload.data,
			challenge: null,
			message: undefined,
		});
		return true;
	},
	logout: async () => {
		await fetch(buildApiUrl("/api/auth/audit/logout"), {
			method: "POST",
			credentials: "include",
		}).catch(() => null);

		clearAuthFlag();

		set({
			status: "unauthenticated",
			user: null,
			message: undefined,
		});
	},
}));

if (typeof window !== "undefined") {
	window.addEventListener("storage", (event) => {
		if (event.key !== STORAGE_KEY) return;
		const isAuthenticated = getAuthFlag();
		useSessionStore.setState({
			status: isAuthenticated ? "checking" : "unauthenticated",
			user: null,
			message: undefined,
		});
		if (isAuthenticated) {
			useSessionStore
				.getState()
				.initialize()
				.catch(() => null);
		}
	});
}
